import type { EntityState } from "@reduxjs/toolkit";
import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import type { Timestamp } from "@somewear/api";
import type {
	GetDefaultSettingsRequest,
	GetDefaultSettingsResponse,
} from "@somewear/api/src/proto/api/workspace_pb";
import type { WorkspaceDefaultSettings } from "@somewear/api/src/proto/workspace_default_settings_proto_pb";
import { createActionSet } from "@somewear/model";
import type { WritableDraft } from "immer/dist/types/types-external";

import { emitWorkspacesReceived } from "./workspace.actions";
import type { WorkspaceState } from "./workspace.state";
import type { IWorkspace } from "./workspaces.slice";
import type { WorkspaceDefaultsRequestSchema } from "./workspaceSettings.model";

export interface IWorkspaceSettingsState extends WorkspaceDefaultSettings.AsObject {
	id: string;
	lastUpdated?: Timestamp.AsObject;
}

export const workspaceSettingsActions = {
	updateDefaultSettings: createActionSet<
		WorkspaceDefaultsRequestSchema,
		GetDefaultSettingsResponse.AsObject
	>("workspace/defaultSettings/update"),
	getDefaultSettings: createActionSet<
		GetDefaultSettingsRequest.AsObject,
		GetDefaultSettingsResponse.AsObject
	>("workspace/defaultSettings/get"),
};

const adapter = createEntityAdapter<IWorkspaceSettingsState>({
	selectId: (workspace) => workspace.id,
});

export const { selectAll: selectAllWorkspaceSettings, selectById: selectWorkspaceSettingsById } =
	adapter.getSelectors((state: WorkspaceState) => state.workspaceSettings);

function updateSettings(
	state: WritableDraft<EntityState<IWorkspaceSettingsState>>,
	payload: GetDefaultSettingsResponse.AsObject,
): IWorkspaceSettingsState | undefined {
	if (!payload.workspace?.id || !payload.settings || !workspaceHasSettings(payload)) return;

	adapter.upsertOne(state, {
		id: payload.workspace.id,
		// Deleting lastUpdated if the settings are inherited from the organization,
		// otherwise user can be displayed the lastUpdated timestamp, which reflects
		// when the org default settings were updated, and is technically correct for workspace
		// settings, but is a little conceptually different from the user's perspective.
		lastUpdated: payload.lastUpdated,
		...payload.settings,
	});
}

function workspaceHasSettings(response: GetDefaultSettingsResponse.AsObject): boolean {
	return Boolean(response.lastUpdated && response.lastUpdated.seconds > 0);
}

function toWorkspaceSettingsResponse(workspace: IWorkspace): GetDefaultSettingsResponse.AsObject {
	let lastUpdated;

	if (workspace.defaultSettings) {
		lastUpdated = {
			seconds: workspace.defaultSettings.sentTimestamp,
			nanos: 0,
		};
	}

	return {
		workspace: {
			...workspace,
			meshKey: "", // Not needed since settings slice adapter is isolated
			externalId: "",
		},
		lastUpdated,
		settings: workspace.defaultSettings,
	};
}

export const workspaceSettingsSlice = createSlice({
	name: "workspaceSettings",
	initialState: adapter.getInitialState(),
	reducers: {
		_: () => {},
	},
	extraReducers: (builder) => {
		builder.addCase(emitWorkspacesReceived, (state, action) => {
			const workspacePayload = action.payload.map(toWorkspaceSettingsResponse);

			workspacePayload.forEach((workspace) => {
				updateSettings(state, workspace);
			});
		});
		builder.addCase(workspaceSettingsActions.getDefaultSettings.fulfilled, (state, action) => {
			updateSettings(state, action.payload.data);
		});
		builder.addCase(
			workspaceSettingsActions.updateDefaultSettings.fulfilled,
			(state, action) => {
				updateSettings(state, action.payload.data);
			},
		);
	},
});
